import React from "react";
import { Card, KeyValueList, Tooltip } from "@sscs/ui";
import { capitalizeFirstLetter, humanReadableBytes } from "@sscs/utils";
import { composeChangeNarrative } from "./changeNarrative";
import styles from "./diffChanges.module.scss";
const hasValue = (change) => change.current !== "" || change.previous !== "";
const hashValue = (value) => value ? (React.createElement(Tooltip, { title: React.createElement("code", null, value) },
    React.createElement("span", { className: styles["hash-value"] }, value))) : null;
const signedBytes = (deltaBytes) => `${deltaBytes < 0 ? "−" : "+"}${humanReadableBytes(Math.abs(deltaBytes))}`;
const classificationLabel = (value) => value ? capitalizeFirstLetter(value) : null;
const buildFacts = ({ hashChange, formatChange, sizeChange, entropyChange, classificationChange, }, isComparison) => {
    const facts = [];
    if (hashChange && hasValue(hashChange))
        facts.push({
            key: "Hash",
            current: hashValue(hashChange.current),
            previous: hashValue(hashChange.previous),
        });
    if (formatChange && hasValue(formatChange))
        facts.push({
            key: "Format",
            current: formatChange.current,
            previous: formatChange.previous,
        });
    if (sizeChange && hasValue(sizeChange)) {
        const delta = Number(sizeChange.current) - Number(sizeChange.previous);
        facts.push({
            key: "Size",
            current: humanReadableBytes(Number(sizeChange.current)),
            previous: isComparison ? (React.createElement(React.Fragment, null,
                humanReadableBytes(Number(sizeChange.previous)),
                " ",
                React.createElement("span", { className: styles["fact-delta"] },
                    "(",
                    signedBytes(delta),
                    ")"))) : (humanReadableBytes(Number(sizeChange.previous))),
        });
    }
    if (entropyChange && hasValue(entropyChange))
        facts.push({
            key: "Content entropy",
            current: entropyChange.current,
            previous: entropyChange.previous,
        });
    if (classificationChange && hasValue(classificationChange))
        facts.push({
            key: "Classification",
            current: classificationLabel(classificationChange.current),
            previous: classificationLabel(classificationChange.previous),
        });
    return facts;
};
const ChangeSummaryCard = ({ categories, changeType, ...facts }) => {
    var _a;
    const isComparison = changeType === "changed";
    const factRows = buildFacts(facts, isComparison);
    const narrative = composeChangeNarrative(categories);
    const statementFacts = {};
    if (((_a = facts.functionalityChange) === null || _a === void 0 ? void 0 : _a.change) === "changed")
        statementFacts["Functional similarity"] =
            "File changed its low-level code signature";
    const statementCount = Object.keys(statementFacts).length;
    const categoriesChanged = categories.filter((category) => category.added + category.removed + category.changed > 0);
    const countTotal = categories.reduce((total, category) => total + category.added + category.removed + category.changed, factRows.length + statementCount);
    const groupTotal = factRows.length + statementCount + categoriesChanged.length;
    const presentSide = changeType === "removed" ? "previous" : "current";
    const currentValues = Object.fromEntries(factRows.map((fact) => [fact.key, fact.current]));
    const previousValues = Object.fromEntries(factRows.map((fact) => [fact.key, fact.previous]));
    const presentValues = Object.fromEntries(factRows.map((fact) => [fact.key, fact[presentSide]]));
    const comparisonFacts = (React.createElement("div", { className: styles["facts-comparison"] },
        React.createElement(KeyValueList, { list: currentValues }),
        React.createElement("span", { className: styles["vs-gutter"] }, "vs"),
        React.createElement(KeyValueList, { list: previousValues, hideKeys: true })));
    const factsContent = isComparison ? (comparisonFacts) : (React.createElement(KeyValueList, { list: presentValues }));
    return (React.createElement(Card.Card, null,
        React.createElement(Card.Title, { as: "h2", details: [
                `${countTotal.toLocaleString()} ${countTotal === 1 ? "change" : "changes"} in ${groupTotal.toLocaleString()} ${groupTotal === 1 ? "category" : "categories"}`,
            ] }, "What changed"),
        React.createElement(Card.Content, { className: styles["summary-content"] },
            factRows.length > 0 ? factsContent : null,
            statementCount > 0 ? React.createElement(KeyValueList, { list: statementFacts }) : null,
            narrative ? React.createElement("p", { className: styles.narrative }, narrative) : null)));
};
export default ChangeSummaryCard;
//# sourceMappingURL=ChangeSummaryCard.js.map